/**
 * Single source of truth for what counts as "document content" when
 * computing Ask Graspd character offsets.
 *
 * Background: offsets used to be computed by walking *every* text node in
 * the lesson container with `document.createTreeWalker(container,
 * NodeFilter.SHOW_TEXT)` and no filter. Lesson renderers historically
 * injected their CSS as an inline `<style>` block inside that same
 * container, so hundreds of characters of CSS text were silently counted
 * as document content. Every saved offset was inflated by however much CSS
 * preceded the selection, and any edit to that CSS shifted every
 * previously-saved highlight with no error and no test failure.
 *
 * The fix: exclude non-prose text (currently `<style>`, `<script>`,
 * `<noscript>`) from every offset computation, save-side and render-side
 * alike, via this one filter. If save-side and render-side ever disagree
 * on what counts as content, every highlight breaks - so there must be
 * exactly one definition, used everywhere offsets are computed or
 * resolved.
 *
 * `<noscript>` is included alongside `<style>`/`<script>` because its
 * content never renders in a JS-enabled browser (the only environment this
 * feature runs in) - counting it would produce offsets that don't match
 * what the user actually saw and selected, for the same reason CSS text
 * shouldn't count.
 *
 * We deliberately do NOT exclude aria-hidden or other accessibility-only
 * nodes: that content can still be visible, selectable prose (e.g. an
 * icon's accessible label rendered as visually-hidden but still real
 * text). Excluding it would be a materially bigger behavior change than
 * the bug being fixed here, so it's left alone.
 *
 * We DO exclude rendered diagrams (`svg`, and the mermaid containers
 * `.lesson-diagram` / `.mermaid`). HtmlView renders a `<pre class="mermaid">`
 * into an `<svg>` full of `<text>`/`<foreignObject>` labels; those labels
 * are diagram internals, not selectable lesson prose. Counting them would
 * pollute every offset (save- and render-side), and — worse — letting the
 * highlight walker descend into the svg lets applyHighlightsToNodes split
 * and rewrite the diagram's own text nodes, corrupting it. Excluding the
 * whole diagram region keeps offsets stable and the svg untouched.
 */
const EXCLUDED_ANCESTOR_SELECTOR = 'style, script, noscript, svg, .lesson-diagram, .mermaid';

function isContentTextNode(node) {
  const parent = node.parentElement;
  if (!parent || typeof parent.closest !== 'function') return true;
  return !parent.closest(EXCLUDED_ANCESTOR_SELECTOR);
}

/**
 * NodeFilter callback used by every TreeWalker in Ask Graspd that computes
 * or resolves character offsets. Exported primarily so tests can assert
 * save-side and render-side walkers are built from the exact same filter.
 */
export function contentTextNodeFilter(node) {
  return isContentTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
}

/**
 * Create a TreeWalker over only the "content" text nodes of a container.
 */
export function createContentTreeWalker(container) {
  return document.createTreeWalker(container, NodeFilter.SHOW_TEXT, contentTextNodeFilter);
}

/**
 * Walk a container once and collect its content text nodes along with
 * their global character offsets (start inclusive, end exclusive).
 * O(content length) single pass.
 */
export function collectContentTextNodes(container) {
  const walker = createContentTreeWalker(container);
  const nodes = [];
  let offset = 0;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const length = node.textContent ? node.textContent.length : 0;
    nodes.push({ node, start: offset, end: offset + length });
    offset += length;
  }
  return nodes;
}

/**
 * The concatenated content text of a container - same order, same
 * exclusions as collectContentTextNodes/createContentTreeWalker - so
 * string indices into the result line up exactly with startOffset/
 * endOffset values produced by those functions.
 */
export function getContentText(container) {
  const walker = createContentTreeWalker(container);
  let text = '';
  while (walker.nextNode()) {
    text += walker.currentNode.textContent || '';
  }
  return text;
}
